/**
 * MyClips V3 — Faz L-2: Locale-aware number + date formatter
 *
 * Türkiye kullanıcı kitlesi için Intl.NumberFormat("tr") — sayaç,
 * beğeni, izlenme, para gibi değerlerde kullanılır. İki mod:
 *   - formatCount(n)        → "12.345" (binlik ayraç nokta)
 *   - formatCompact(n)      → "12,3 B" | "1,2 Mn" | "3,1 Mr"
 *   - formatRelativeTime(ms) → "3 dk önce" | "2 gün önce"
 *   - formatDuration(ms)    → "0:15" | "1:32:05" (video süresi)
 *
 * Prensip: Her sayaç UI'da kompakt, tooltip/modal'da tam.
 *
 * Performance: Intl.* formatter'ları modül-level cache'lenir —
 * her render'da yeni formatter kurulmaz.
 */

const LOCALE = "tr";

/**
 * Cache'lenen formatter'ların ORTAK yüzeyi. Gövde iki kaynaktan doldurur:
 * `Intl.*` örneği VEYA `catch` dalındaki elle yazılmış yedek — bu yüzden tip
 * `Intl.NumberFormat` değil, ikisinin de karşıladığı `format` yüzeyidir.
 */
type NumberFormatterLike = { format: (n: number) => string };
type RelativeTimeFormatterLike = {
  format: (n: number, u: Intl.RelativeTimeFormatUnit) => string;
};

/* Lazy cache — ilk çağrıda kurulur */
let _nfCount: NumberFormatterLike | null = null;
let _nfCompact: NumberFormatterLike | null = null;
let _rtf: RelativeTimeFormatterLike | null = null;

function getNfCount() {
  if (_nfCount) return _nfCount;
  try {
    _nfCount = new Intl.NumberFormat(LOCALE);
  } catch (_) {
    _nfCount = { format: (n: number) => String(n) };
  }
  return _nfCount;
}

function getNfCompact() {
  if (_nfCompact) return _nfCompact;
  try {
    _nfCompact = new Intl.NumberFormat(LOCALE, {
      notation: "compact",
      maximumFractionDigits: 1,
    });
  } catch (_) {
    _nfCompact = { format: (n: number) => String(n) };
  }
  return _nfCompact;
}

function getRtf() {
  if (_rtf) return _rtf;
  try {
    _rtf = new Intl.RelativeTimeFormat(LOCALE, { numeric: "auto" });
  } catch (_) {
    _rtf = { format: (n: number, u: Intl.RelativeTimeFormatUnit) => `${n} ${u}` };
  }
  return _rtf;
}

/**
 * Tam sayı — binlik ayraçlı.
 *   1234567 → "1.234.567"
 */
export function formatCount(n: unknown): string {
  const v = Number(n);
  if (!Number.isFinite(v)) return "0";
  return getNfCount().format(v);
}

/**
 * Kompakt sayı — Türkçe ayraçlı.
 *   1234     → "1,2 B"
 *   12345    → "12,3 B"
 *   1234567  → "1,2 Mn"
 *   1234567890 → "1,2 Mr"
 *
 * Not: Intl.NumberFormat Türkçe compact notation'ı native destekler
 * (Intl 2021+). İzole bir fallback ile küçük sayılar için raw format.
 */
export function formatCompact(n: unknown): string {
  const v = Number(n);
  if (!Number.isFinite(v)) return "0";
  if (Math.abs(v) < 1000) return getNfCount().format(v);
  return getNfCompact().format(v);
}

/**
 * Göreli zaman — "3 dk önce" tarzı.
 *   epochMs veya geçen ms miktarı kabul eder.
 *   referanceMs (default Date.now()) ile fark hesaplanır.
 */
export function formatRelativeTime(
  epochOrDeltaMs: unknown,
  referenceMs: number = Date.now(),
): string {
  const ms = Number(epochOrDeltaMs);
  if (!Number.isFinite(ms)) return "";
  const delta =
    Math.abs(ms) > 1e11
      ? ms - referenceMs  // epochMs verilmiş
      : -ms;              // geçen süre verilmiş (pozitif) → negatif yap ("önce")
  const absSec = Math.abs(delta) / 1000;
  const rtf = getRtf();

  if (absSec < 60)         return rtf.format(Math.round(delta / 1000),   "second");
  if (absSec < 3600)       return rtf.format(Math.round(delta / 60000),  "minute");
  if (absSec < 86400)      return rtf.format(Math.round(delta / 3600000), "hour");
  if (absSec < 604800)     return rtf.format(Math.round(delta / 86400000), "day");
  if (absSec < 2592000)    return rtf.format(Math.round(delta / 604800000), "week");
  if (absSec < 31536000)   return rtf.format(Math.round(delta / 2592000000), "month");
  return                          rtf.format(Math.round(delta / 31536000000), "year");
}

/**
 * Video süresi — mm:ss veya hh:mm:ss.
 *   15000     → "0:15"
 *   92000     → "1:32"
 *   3925000   → "1:05:25"
 */
export function formatDuration(ms: unknown): string {
  const v = Number(ms);
  if (!Number.isFinite(v) || v < 0) return "0:00";
  const totalSec = Math.floor(v / 1000);
  const h = Math.floor(totalSec / 3600);
  const m = Math.floor((totalSec % 3600) / 60);
  const s = totalSec % 60;
  const ss = s.toString().padStart(2, "0");
  if (h > 0) {
    const mm = m.toString().padStart(2, "0");
    return `${h}:${mm}:${ss}`;
  }
  return `${m}:${ss}`;
}

/**
 * Bytes → okunabilir.
 *   1024 → "1 KB"
 *   1572864 → "1,5 MB"
 */
export function formatBytes(n: unknown): string {
  const v = Number(n);
  if (!Number.isFinite(v) || v < 0) return "0 B";
  if (v < 1024) return `${v} B`;
  const units = ["KB", "MB", "GB", "TB"];
  let idx = -1;
  let x = v;
  do {
    x /= 1024;
    idx++;
  } while (x >= 1024 && idx < units.length - 1);
  return `${getNfCompact().format(x)} ${units[idx]}`;
}

export default {
  formatCount,
  formatCompact,
  formatRelativeTime,
  formatDuration,
  formatBytes,
};
